import { useEffect, useState } from 'react';
import {
    CONTENT_HEIGHT_ATTRIBUTE,
    FULL_HEIGHT_ATTRIBUTE,
    TRANSITION_ATTRIBUTE,
    setSheetStateAttribute,
} from '../sheetStateAttributes';
import type { SheetEngineProps } from '../sheetTypes';
import {
    getSheetHeightAdjustedForClose,
    getSheetSnapPointWindowHeight,
    setContentHeightToMax,
    setContentHeightToVisibleSpace,
    setSheetHeight,
    setSheetHeightVariable,
} from '../utils/sheetHeightUtils';
import { getContentSnapPointCeiling, initialiseSnapPoints } from '../utils/snapPointUtils';
import runOnTransitionEndWithTimeout from '../utils/runOnTransitionEndWithTimeout';

/** A snap point within this of the content's own height is treated as already correct. */
const SNAP_POINT_TOLERANCE = 0.01;

type SheetSnapPointState = {
    snapPointsState: number[];
    addSnapPoint: (snapPoint: number) => void;
};

/**
 * Take care of snapPoint and activeSnapPoint state, and expose functions to update it
 */
const useSheetSnapPointState = (sheetProps: SheetEngineProps): SheetSnapPointState => {
    const {
        sheetContainerRef,
        sheetRef,
        sheetContentRef,
        snapPoints = [],
        defaultSnapPoint,
        preventContentHeightChange = false,
        activeSnapPoint,
        onSnapPointChange,
        preventDragDismiss = false,
        useViewportResizeForKeyboard = false,
        topInset = 0,
    } = sheetProps;

    const [snapPointsState, setSnapPointsState] = useState<number[]>(
        initialiseSnapPoints(snapPoints, preventDragDismiss),
    );

    const addSnapPoint = (snapPoint: number) => {
        if (snapPointsState.includes(snapPoint)) return;

        setSnapPointsState([...snapPointsState, snapPoint].sort());
    };

    useEffect(() => {
        setSheetStateAttribute(sheetRef.current, TRANSITION_ATTRIBUTE, true);
        setSheetStateAttribute(sheetRef.current, FULL_HEIGHT_ATTRIBUTE, activeSnapPoint === 1);

        let cancelRunCallback: (() => void) | undefined;

        // Wait one frame after enabling the transform transition, so programmatic snap changes
        // animate instead of jumping straight to their destination.
        const animationFrameId = requestAnimationFrame(() => {
            const newSheetHeight = getSheetHeightAdjustedForClose(activeSnapPoint, topInset);
            const newHeight = setSheetHeight(sheetRef, newSheetHeight);

            if (newHeight == null) {
                setSheetStateAttribute(sheetRef.current, TRANSITION_ATTRIBUTE, false);
                return;
            }

            setContentHeightToMax(sheetProps);
            setSheetHeightVariable(sheetContainerRef, newHeight);

            cancelRunCallback = runOnTransitionEndWithTimeout(sheetRef, () => {
                setContentHeightToVisibleSpace(sheetProps, useViewportResizeForKeyboard);
                setSheetStateAttribute(sheetRef.current, TRANSITION_ATTRIBUTE, false);
            });
        });

        return () => {
            cancelAnimationFrame(animationFrameId);
            cancelRunCallback?.();
        };
    }, [activeSnapPoint, useViewportResizeForKeyboard]);

    useEffect(() => {
        if (!useViewportResizeForKeyboard || activeSnapPoint === 0) return;

        const updateSheetLayoutForViewportResize = () => {
            const newSheetHeight = getSheetHeightAdjustedForClose(activeSnapPoint, topInset);
            const newHeight = setSheetHeight(sheetRef, newSheetHeight);
            if (newHeight == null) return;

            setSheetStateAttribute(sheetRef.current, FULL_HEIGHT_ATTRIBUTE, activeSnapPoint === 1);
            setSheetHeightVariable(sheetContainerRef, newHeight);
            setContentHeightToVisibleSpace(sheetProps, true);
        };

        // Not run directly, and registered a frame late so the snap point effect above has had its
        // own frame first. Handling a resize before then measures a sheet still sitting below the
        // bottom of the screen, so the content height comes out as 0, and the snap point effect
        // then finds the transform already written, bails, and never installs the callback that
        // would have measured the content once the sheet settled.
        const listenerFrameId = requestAnimationFrame(() => {
            window.visualViewport?.addEventListener('resize', updateSheetLayoutForViewportResize);
        });

        return () => {
            cancelAnimationFrame(listenerFrameId);
            window.visualViewport?.removeEventListener('resize', updateSheetLayoutForViewportResize);
        };
    }, [activeSnapPoint, useViewportResizeForKeyboard]);

    /**
     * A sheet with no explicit snap points measures its content once, and keeps the result as a
     * fraction of the viewport it measured in. A keyboard that closes as the sheet opens leaves a
     * taller viewport, and that same fraction then describes a taller sheet than its content needs.
     * Measuring the content again against the new viewport settles the sheet back onto it.
     */
    useEffect(() => {
        // Null for a sheet that fixes a share of the screen rather than measuring its content -
        // an explicit snap point list, or a plain numeric default as the Document sheet has.
        const contentSnapPointCeiling = getContentSnapPointCeiling(snapPoints, defaultSnapPoint);

        // A sheet holding a fixed content height is excluded as well: its content no longer reflects
        // the height it wants, so measuring it would snap the sheet to the wrong one.
        if (contentSnapPointCeiling === null || preventContentHeightChange || activeSnapPoint === 0) return;

        const remeasureContentSnapPoint = () => {
            const panel = sheetRef.current;
            const content = sheetContentRef.current;
            if (!panel || !content) return;

            // Measured by letting the panel take its content's height and reading it back in the
            // same tick. Reading the panel rather than its position keeps this independent of where
            // the sheet has animated to, which is what makes it safe to run at any moment.
            const previousContentHeight = content.style.height;
            content.style.height = 'auto';
            setSheetStateAttribute(panel, CONTENT_HEIGHT_ATTRIBUTE, true);

            const contentHeight = panel.clientHeight;

            setSheetStateAttribute(panel, CONTENT_HEIGHT_ATTRIBUTE, false);
            content.style.height = previousContentHeight;

            // A sheet that measures as having no content has not laid out yet. Snapping to that
            // would close it.
            if (!contentHeight) return;

            const contentSnapPoint = Math.min(
                contentSnapPointCeiling,
                contentHeight / getSheetSnapPointWindowHeight(topInset),
            );

            // Already sized to its content, so there is nothing to correct. Bailing here is what
            // stops the snap change this triggers from measuring its way around in a loop.
            if (Math.abs(contentSnapPoint - activeSnapPoint) < SNAP_POINT_TOLERANCE) return;

            onSnapPointChange(contentSnapPoint);
        };

        // The viewport can finish changing before this effect has a snap point to work with, which
        // leaves no resize to listen for. Measuring here covers that, and costs nothing when the
        // sheet already matches its content.
        remeasureContentSnapPoint();

        window.visualViewport?.addEventListener('resize', remeasureContentSnapPoint);

        return () => {
            window.visualViewport?.removeEventListener('resize', remeasureContentSnapPoint);
        };
    }, [
        activeSnapPoint,
        defaultSnapPoint,
        onSnapPointChange,
        preventContentHeightChange,
        sheetContentRef,
        sheetRef,
        snapPoints,
        topInset,
    ]);

    return { snapPointsState, addSnapPoint };
};

export default useSheetSnapPointState;
